import { Position } from '@xyflow/react';
import Decimal from 'decimal.js';

import {
  EstateWaterfallEdgeKind,
  EstateWaterfallTransferKind,
} from '@/types/schema';
import { sumDecimalJS } from '@/utils/decimalJSUtils';
import { formatCurrency } from '@/utils/formatting/currency';
import { FlowChartGraph } from '@/utils/graphology/FlowChartGraph';

import {
  EstateWaterfall_EdgeFragment,
  EstateWaterfallFragment,
} from '../graphql/EstateWaterfall.generated';
import {
  EstateWaterfallGraphAttributes,
  EstateWaterfallGraphEdgeAttributes,
  EstateWaterfallGraphNodeAttributes,
  GraphNode,
  GraphNodeData,
} from '../types';
import { getFeatureFlaggedEdgeLabel, getNodeId } from './utils';
import { createEdge } from './waterfallGraph';

function getTargetHandleFromEdgeKind(
  kind: EstateWaterfallEdgeKind
): Position | undefined {
  switch (kind) {
    case EstateWaterfallEdgeKind.Transfer:
      // We want transfers to appear as horizontal lines from left to right
      return Position.Left;
    default:
      return Position.Top;
  }
}

function getSourceHandleFromEdgeKind(
  kind: EstateWaterfallEdgeKind
): Position | undefined {
  switch (kind) {
    case EstateWaterfallEdgeKind.Transfer:
      return Position.Right;
    default:
      return Position.Bottom;
  }
}

const getEdgeSource = (edge: EstateWaterfall_EdgeFragment) =>
  getNodeId({
    id: edge.from.group?.id || edge.from.id,
    afterDeath: edge.from.afterDeath,
  });

const getEdgeTarget = (edge: EstateWaterfall_EdgeFragment) =>
  getNodeId({
    id: edge.to.group?.id || edge.to.id,
    afterDeath: edge.to.afterDeath,
  });

interface DrawEdgesInput {
  viz: EstateWaterfallFragment['visualizationWithProjections'];
  graph: FlowChartGraph<
    EstateWaterfallGraphNodeAttributes<GraphNode, GraphNodeData>,
    EstateWaterfallGraphEdgeAttributes,
    EstateWaterfallGraphAttributes
  >;
}

enum EdgeLinkType {
  NodeToGroup = 'node-to-group',
  GroupToGroup = 'group-to-group',
  GroupToNode = 'group-to-node',
}

// Reverse-edge normalization can merge a hypothetical sale/loan funding edge with
// repayment edges for the same note. When that happens, the merged edge should
// still display the initial funding amount rather than whichever repayment value
// happened to survive the merge order.
function getMergedEdgeDisplayValue({
  associatedTransferLogItems,
  defaultValue,
}: {
  associatedTransferLogItems?: EstateWaterfall_EdgeFragment['associatedTransferLogItems'];
  defaultValue: Decimal;
}) {
  const hypotheticalSaleLoanTransfer = associatedTransferLogItems?.find(
    (item) => item.kind === EstateWaterfallTransferKind.HypotheticalSaleLoan
  );

  return hypotheticalSaleLoanTransfer?.finalTransferAmount ?? defaultValue;
}

export function drawEdges({ viz, graph }: DrawEdgesInput): void {
  const createAndAddEdge = (
    edgeFragment: EstateWaterfall_EdgeFragment & { bidirectional?: boolean },
    source: string,
    target: string,
    kind: EstateWaterfallEdgeKind,
    value: Decimal
  ) => {
    const edgeLabel = getFeatureFlaggedEdgeLabel({
      source,
      target,
      edge: edgeFragment,
      label: '',
      value: formatCurrency(value, { notation: 'compact' }),
      variant: undefined,
    });

    // If the edge has a hypothetical transfer, we use the secondary edge variant
    const arrowEdgeVariant = edgeFragment.associatedTransferLogItems?.some(
      (item) => item.isHypothetical
    )
      ? 'secondary'
      : 'primary';

    let showFromArrow = kind === EstateWaterfallEdgeKind.HypotheticalSaleLoan;

    // If the reverse edge exists, we set the showFromArrow to true
    if (edgeFragment.bidirectional) {
      showFromArrow = true;
    }

    // Determine handle positions based on edge kind and bidirectionality
    let sourceHandle = getSourceHandleFromEdgeKind(kind);
    let targetHandle = getTargetHandleFromEdgeKind(kind);

    // For bidirectional edges, we need stable handle positions that won't change during updates
    if (edgeFragment.bidirectional) {
      // Use consistent handle positions for bidirectional edges
      // For bidirectional edges, we'll use Left/Right handles
      if (source < target) {
        sourceHandle = Position.Right;
        targetHandle = Position.Left;
      } else {
        sourceHandle = Position.Left;
        targetHandle = Position.Right;
      }
    }

    const edge = createEdge({
      source,
      target,
      sourceHandle,
      targetHandle,
      data: {
        variant: arrowEdgeVariant,
        showFromArrow,
        style: 'solid',
        edgeLabel,
      },
    });

    if (!graph.hasNode(source) || !graph.hasNode(target)) {
      return;
    }

    graph.addEdgeSafe(source, target, { type: 'default', edge });
  };

  // Reduce the edges so we can only have one edge between any two nodes
  const normalizedEdges = viz.edges.reduce((acc, originalEdge) => {
    // Create a deep copy of the edge
    const edge = {
      ...originalEdge,
    };

    // Get the source and target of the edge
    const source = getEdgeSource(edge);
    const target = getEdgeTarget(edge);

    const reverseEdgeIdx = acc.findIndex(
      (e) => getEdgeSource(e) === target && getEdgeTarget(e) === source
    );

    if (reverseEdgeIdx !== -1) {
      const reverseEdge = acc[reverseEdgeIdx];
      if (!reverseEdge) {
        return acc;
      }

      reverseEdge.associatedTransferLogItems = [
        ...(edge.associatedTransferLogItems?.map((it) => ({
          ...it,
          // Mark this item as reversed to indicate that it should be displayed in the opposite direction
          reversed: true,
        })) || []),
        ...(reverseEdge.associatedTransferLogItems || []),
      ];
      // A merged sale/loan edge should keep the initial funding amount even if
      // the reverse repayment edge was processed first.
      reverseEdge.value = getMergedEdgeDisplayValue({
        associatedTransferLogItems: reverseEdge.associatedTransferLogItems,
        defaultValue: reverseEdge.value,
      });
      edge.associatedTransferLogItems = [];
      (reverseEdge as unknown as { bidirectional: boolean }).bidirectional =
        true;

      return acc;
    }

    return [...acc, edge];
  }, [] as EstateWaterfall_EdgeFragment[]);

  const edgeFragments = normalizedEdges;

  // Add edges between ungrouped nodes
  edgeFragments.forEach((edgeFragment) => {
    const { to, from, value, kind } = edgeFragment;
    const source = getEdgeSource(edgeFragment);
    const target = getEdgeTarget(edgeFragment);

    const edgeValue = to.group?.id || from.group?.id ? null : value;

    if (edgeValue) {
      createAndAddEdge(edgeFragment, source, target, kind, edgeValue);
    }
  });

  const reduceEdges = (
    edges: typeof groupToGroupEdges,
    linkType: EdgeLinkType
  ) =>
    edges.reduce<Record<string, (typeof nodeToGroupEdges)[0]>>((acc, edge) => {
      const source = getNodeId({
        id:
          linkType === EdgeLinkType.NodeToGroup
            ? edge.from.id
            : edge.from.group!.id,
        afterDeath: edge.from.afterDeath,
      });

      const target = getNodeId({
        id:
          linkType === EdgeLinkType.GroupToNode
            ? edge.to.id
            : edge.to.group!.id,
        afterDeath: edge.to.afterDeath,
      });

      const key = `${source}|${target}`;
      const existingValue = acc[key]?.value ?? new Decimal(0);
      const existingItems = acc[key]?.associatedTransferLogItems || [];
      const newItems = edge.associatedTransferLogItems || [];
      const mergedItems = [...existingItems, ...newItems];
      // Grouped edges use the same rule: a merged hypothetical sale/loan should
      // display the funding amount instead of a summed repayment amount.
      const newValue = getMergedEdgeDisplayValue({
        associatedTransferLogItems: mergedItems,
        defaultValue: sumDecimalJS([existingValue, edge.value]),
      });

      acc[key] = {
        ...edge,
        value: newValue,
        associatedTransferLogItems: mergedItems,
      };

      return acc;
    }, {});

  // Edges that require reducing the underlying edge values before drawing
  const groupToGroupEdges = edgeFragments.filter(
    ({ from, to }) => from.group && to.group
  );
  const nodeToGroupEdges = edgeFragments.filter(
    ({ from, to }) => !from.group && to.group
  );
  const groupToNodeEdges = edgeFragments.filter(
    ({ from, to }) => from.group && !to.group
  );

  const groupToGroupEdgesReduced = reduceEdges(
    groupToGroupEdges,
    EdgeLinkType.GroupToGroup
  );
  const nodeToGroupEdgesReduced = reduceEdges(
    nodeToGroupEdges,
    EdgeLinkType.NodeToGroup
  );
  const groupToNodeEdgesReduced = reduceEdges(
    groupToNodeEdges,
    EdgeLinkType.GroupToNode
  );

  const addReducedEdges = (
    reducedEdges: typeof groupToGroupEdgesReduced,
    { skipSelfLoops = false }: { skipSelfLoops?: boolean } = {}
  ) => {
    Object.entries(reducedEdges).forEach(([key, edgeFragment]) => {
      const [source, target] = key.split('|');
      if (source && target) {
        // Group↔group self-loops are aggregation artifacts (multiple underlying
        // edges between members of the same group collapse to a single edge
        // from the group to itself). They have no useful visual treatment, so
        // skip them. Entity-level self-loops are not affected — they still go
        // through the ungrouped path above and surface the "does not fully
        // distribute" disposition card.
        if (skipSelfLoops && source === target) {
          return;
        }
        createAndAddEdge(
          edgeFragment,
          source,
          target,
          edgeFragment.kind,
          edgeFragment.value
        );
      }
    });
  };

  // Add reduced edges to the graph
  addReducedEdges(groupToGroupEdgesReduced, { skipSelfLoops: true });
  addReducedEdges(nodeToGroupEdgesReduced);
  addReducedEdges(groupToNodeEdgesReduced);
}
